/*
  Bundle discount rules shared by Frequently Bought Together and the cart drawer. Shopify calculates the
  real discount; this only decides which tier to advertise and, in discount code mode, which code to
  put on the cart.
*/
window.NcmBundle ??= (() => {
  let config;
  try {
    config = JSON.parse(document.getElementById('NcmBundleConfig')?.textContent || '{}');
  } catch {
    config = {};
  }
  config.tiers ||= [];
  config.messages ||= {};
  config.freeShipping ||= {};
  config.routes ||= {};
  config.upsells ||= {};

  const eligibleTypes = String(config.eligibleTypes || '')
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);

  const routes = {
    root: config.routes.root || window.Shopify?.routes?.root || '/',
    cart: config.routes.cart || window.routes?.cart_url || '/cart',
    cartUpdate: config.routes.cartUpdate || window.routes?.cart_update_url || '/cart/update',
  };

  const isEligibleType = (type) =>
    !eligibleTypes.length || eligibleTypes.includes(String(type || '').trim().toLowerCase());

  config.protection ||= {};
  const protectionProductId = Number(config.protection.productId) || null;

  const isProtectionLine = (line) =>
    Boolean(line?.properties?._ncm_protection) ||
    (protectionProductId !== null && Number(line?.product_id) === protectionProductId);

  // For product JSON (recommendations, collections): the protection product and anything that never ships.
  const isAddOnProduct = (product) =>
    (protectionProductId !== null && Number(product?.id) === protectionProductId) ||
    (product?.variants?.length > 0 && product.variants.every((variant) => variant.requires_shipping === false));

  // Add-ons that don't ship (gift cards, package protection) never count toward a shirt bundle.
  const isEligibleLine = (line) =>
    line.requires_shipping !== false && !isProtectionLine(line) && isEligibleType(line.product_type);

  const eligibleQuantity = (cart) =>
    (cart?.items || []).filter(isEligibleLine).reduce((sum, line) => sum + line.quantity, 0);

  // In code mode a tier also needs a code: nothing is advertised that the store can't apply.
  const activeTiers = () => {
    if (config.method !== 'code' && config.method !== 'automatic') return [];
    return config.tiers
      .filter(
        (tier) => tier.enabled && tier.percent > 0 && tier.quantity > 0 && (config.method !== 'code' || tier.code)
      )
      .sort((a, b) => a.quantity - b.quantity);
  };

  // Only the best qualifying tier applies; tiers never stack.
  const tierFor = (quantity) =>
    activeTiers()
      .filter((tier) => tier.quantity <= quantity)
      .sort((a, b) => b.percent - a.percent || b.quantity - a.quantity)[0] || null;

  const nextTier = (quantity, current = tierFor(quantity)) =>
    activeTiers().find((tier) => tier.quantity > quantity && tier.percent > (current?.percent || 0)) || null;

  // The tier Frequently Bought Together promotes (Theme settings → Bundle discounts).
  const goalTier = () => activeTiers().find((tier) => tier.key === config.fbtGoal) || null;

  const fetchCart = () =>
    fetch(`${routes.cart}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );

  const sameCodes = (a, b) => {
    const normalize = (codes) => codes.map((code) => code.toUpperCase()).sort().join(',');
    return normalize(a) === normalize(b);
  };

  /*
    Code mode: puts the code of the tier the cart qualifies for on the cart and removes the other tier
    codes. Codes the customer entered themselves are always kept; Shopify decides whether they combine.
    Other modes only remove tier codes, so an old code can't stack with an automatic discount.

    Resolves to null when nothing changed, otherwise { cart, redirect }: `cart` is the /cart/update.js
    response (with `sections` when requested); `redirect` is a /discount/ URL to fall back on when the
    cart API didn't take the code.
  */
  const syncCode = async ({ cart, sections = [] } = {}) => {
    const tierCodes = config.tiers.map((tier) => String(tier.code || '').toUpperCase()).filter(Boolean);
    if (!tierCodes.length) return null;

    cart ||= await fetchCart();
    const existing = (cart.discount_codes || []).map((discount) => discount.code);
    const others = existing.filter((code) => !tierCodes.includes(code.toUpperCase()));
    const wanted = config.method === 'code' ? tierFor(eligibleQuantity(cart))?.code || null : null;
    const desired = wanted ? [...others, wanted] : others;
    if (sameCodes(existing, desired)) return null;

    const redirect = wanted
      ? `${routes.root.replace(/\/?$/, '/')}discount/${encodeURIComponent(wanted)}?redirect=${encodeURIComponent(routes.cart)}`
      : null;

    try {
      const response = await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: desired.join(','),
          ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
        }),
      });
      const updated = await response.json();
      const applied = (updated.discount_codes || []).map((discount) => discount.code.toUpperCase());
      const ok = response.ok && (!wanted || applied.includes(wanted.toUpperCase()));
      return { cart: response.ok ? updated : null, redirect: ok ? null : redirect };
    } catch {
      return { cart: null, redirect };
    }
  };

  /*
    Package protection is one unit per order and only alongside something that ships. Fixes a cart that
    breaks either rule (extra units, duplicate lines, or no physical products left). Never adds
    protection. Resolves to null when nothing changed, otherwise the /cart/update.js response.
  */
  const enforceProtection = async ({ cart, sections = [] } = {}) => {
    cart ||= await fetchCart();
    const lines = (cart.items || []).filter(isProtectionLine);
    if (!lines.length) return null;

    const hasPhysical = cart.items.some((line) => !isProtectionLine(line) && line.requires_shipping !== false);
    const updates = {};
    lines.forEach((line, index) => {
      const quantity = hasPhysical && index === 0 ? 1 : 0;
      if (line.quantity !== quantity) updates[line.key] = quantity;
    });
    if (!Object.keys(updates).length) return null;

    const response = await fetch(`${routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        ...(sections.length ? { sections, sections_url: window.location.pathname } : {}),
      }),
    });
    if (!response.ok) return null;
    return response.json();
  };

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  return {
    config,
    routes,
    isEligibleType,
    isEligibleLine,
    isProtectionLine,
    isAddOnProduct,
    enforceProtection,
    eligibleQuantity,
    activeTiers,
    tierFor,
    nextTier,
    goalTier,
    fetchCart,
    syncCode,
    fill,
  };
})();

// Pages without the drawer (the /cart page) render their own cart: apply the protection rules after their
// changes, and reload the cart page so it shows Shopify's corrected cart.
if (!document.querySelector('cart-drawer') && typeof subscribe === 'function') {
  const onCartPage = Boolean(document.querySelector('cart-items'));
  const enforce = () =>
    window.NcmBundle.enforceProtection()
      .then((cart) => {
        if (cart && onCartPage) window.location.reload();
      })
      .catch(() => {});
  subscribe(PUB_SUB_EVENTS.cartUpdate, enforce);
  if (onCartPage) enforce();
}
